import {
  Business as Building2,
  Event as Calendar,
  People as Users
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { Scene, EntitySummary, EventSummary, AnySummary } from '@/lib/types';

interface SceneDetailsCardProps {
  scene: Scene;
  locations: Record<string, EntitySummary>;
  characters: Record<string, EntitySummary>;
  organizations: Record<string, EntitySummary>;
  events: Record<string, EventSummary>;
  getEntityName: (id: string, entities: Record<string, AnySummary>) => string;
}

export function SceneDetailsCard({
  scene,
  locations,
  characters,
  organizations,
  events,
  getEntityName
}: SceneDetailsCardProps) {
  const { t } = useTranslation();

  return (
    <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      <CardContent sx={{ p: 4 }}>
        <Stack spacing={4}>
          {scene.description && (
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>
                {t('entity_list.scenes.labels.description')}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                {scene.description}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>
                {t('entity_form.scenes.goal')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                {scene.goal || t('entity_view.scenes.no_val')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>
                {t('entity_form.scenes.conflict')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                {scene.conflict || t('entity_view.scenes.no_val')}
              </Typography>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>
                {t('entity_list.scenes.labels.point_of_scene')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                {scene.pointOfScene || t('entity_view.scenes.no_val')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>
                {t('entity_list.scenes.labels.themes')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                {scene.themes || t('entity_view.scenes.no_val')}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              {t('entity_view.scenes.settings_locations')}
            </Typography>
            <Box sx={{ mt: 1, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>{scene.settings || t('entity_view.scenes.no_settings')}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {scene.locationIds?.map((id: string) => (
                  <Chip
                    key={id}
                    label={getEntityName(id, locations)}
                    size="small"
                    variant="filled"
                    sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', cursor: 'pointer' }}
                    component={Link}
                    to={`/locations/view/${id}`}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              {t('entity_view.scenes.participants')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {scene.povCharacterId && (
                <Chip
                  key={`pov-${scene.povCharacterId}`}
                  icon={<Users sx={{ fontSize: 14 }} />}
                  label={`POV: ${getEntityName(scene.povCharacterId, characters)}`}
                  size="small"
                  color="primary"
                  sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                  component={Link}
                  to={`/characters/view/${scene.povCharacterId}`}
                />
              )}
              {scene.characterIds?.filter(id => id !== scene.povCharacterId).map((id: string) => (
                <Chip
                  key={id}
                  icon={<Users sx={{ fontSize: 14 }} />}
                  label={getEntityName(id, characters)}
                  size="small"
                  variant="outlined"
                  sx={{ cursor: 'pointer' }}
                  component={Link}
                  to={`/characters/view/${id}`}
                />
              ))}
              {scene.organizationIds?.map((id: string) => (
                <Chip
                  key={id}
                  icon={<Building2 sx={{ fontSize: 14 }} />}
                  label={getEntityName(id, organizations)}
                  size="small"
                  variant="outlined"
                  sx={{ cursor: 'pointer' }}
                  component={Link}
                  to={`/organizations/view/${id}`}
                />
              ))}
            </Box>
          </Box>

          {scene.minorCharacters && (
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>
                {t('entity_form.scenes.minor_characters')}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                {scene.minorCharacters}
              </Typography>
            </Box>
          )}

          {scene.eventIds && scene.eventIds.length > 0 && (
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>
                {t('entity_view.scenes.associated_events')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {scene.eventIds.map((id: string) => (
                  <Chip
                    key={id}
                    icon={<Calendar sx={{ fontSize: 14 }} />}
                    label={getEntityName(id, events)}
                    size="small"
                    variant="filled"
                    sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText', cursor: 'pointer' }}
                    component={Link}
                    to={`/events/view/${id}`}
                  />
                ))}
              </Box>
            </Box>
          )}

          {scene.changesToMake && (
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>
                {t('entity_view.scenes.revision_notes')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'warning.light', color: 'warning.contrastText', borderRadius: 2, opacity: 0.9 }}>
                {scene.changesToMake}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
